import { useState } from "react";
import { useAuth } from "@clerk/react";
import { Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";

export default function AiDraft({ kind, context, onApply, applyLabel = "Use this draft" }) {
  const { getToken } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setText("");
    try {
      const res = await fetch(`${API}/partner/ai/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` },
        body: JSON.stringify({ kind, context: context() }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Draft failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const chunk = p.replace(/^data: /, "");
          if (chunk === "[DONE]") continue;
          if (chunk === "[ERROR]") { toast.error("Drafting stopped unexpectedly. Try again."); continue; }
          setText((t) => t + chunk.replace(/\\n/g, "\n"));
        }
      }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-dashed border-secondary/50 bg-secondary/5 p-4" data-testid={`ai-draft-${kind}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm"><span className="font-semibold text-secondary">Drafting assist</span><span className="text-muted-foreground"> · suggestions only, you approve every word</span></div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={run} disabled={busy} data-testid={`ai-draft-run-${kind}`}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1 h-4 w-4" />}Draft
        </Button>
      </div>
      {(text || busy) && (
        <>
          <Textarea className="mt-3 min-h-[160px] bg-white text-sm" value={text} onChange={(e) => setText(e.target.value)} data-testid={`ai-draft-output-${kind}`} />
          {onApply && <Button size="sm" className="mt-2 rounded-full" disabled={busy || !text} onClick={() => onApply(text)} data-testid={`ai-draft-apply-${kind}`}>{applyLabel}</Button>}
        </>
      )}
    </div>
  );
}
